import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Chip,
  Badge,
  Tabs,
  Tab,
  useTheme,
} from '@mui/material';
import {
  ArrowBack,
  Inventory2,
  SwapHoriz,
  Launch,
  QrCode,
  Print,
  Refresh,
  Assignment,
  Autorenew,
  MoveToInbox,
  Timeline,
} from '@mui/icons-material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { DataService } from 'src/services/data-service';
import { LovService } from 'src/services/lov-service';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { ROLE } from 'src/services/permission-service';
import StatusCard from 'src/components/StatusCard';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import { LabelPaperSize, usePrint } from 'src/hooks/usePrint';
import { EnhancedTableColumn, EnhancedTableColumnType } from 'src/components/enhanced-table';
import Loader from 'src/components/Loader';
import PaginatedDataGrid, { PaginatedDataGridColumn } from 'src/components/paginated-data-grid/PaginatedDataGrid';
import { GridRenderCellParams } from '@mui/x-data-grid';
import TimelineIcon from '@mui/icons-material/Timeline'; 

interface BoxItem {
  itemId: string;
  itemName: string;
  itemBarcode: string;
  itemSymbol: string;
  quantity: number;
  storageUnitName?: string;
  storageUnitCode?: string;
  warehouseName?: string;
  additionalProperties: Record<string, any>;
}

interface BoxDetails {
  boxBarcode: string;
  storageUnitId?: string;
  totalQuantity: number;
  uniqueProductCount: number;
  items: BoxItem[];
  lastUpdated?: string;
}

interface ReassignItemsDialogData {
  open: boolean;
  selectedStorageUnitId: string;
  targetBoxBarcode: string;
  availableBoxes: string[];
  searchingBoxes: boolean;
  selectedItems: { itemId: string; quantity: number; maxQuantity: number }[];
}

interface TransferActivityRow {
  id: string;
  unitId?: string;
  itemId: string;
  itemName: string;
  itemBarcode?: string;
  unitBarcode?: string;
  quantity: number;
  transferType: number;
  direction: number;
  sourceType: number;
  sourceId: string;
  sourceName: string;
  destinationType: number;
  destinationId: string;
  destinationName: string;
  timestamp: string;
  notes: string;
  referenceId: string;
  userId: string;
  userName: string;
}
interface MovementHistoryItem {
  id: string;
  unitId: string;
  itemId: string;
  itemName: string;
  unitBarcode: string;
  quantity: number;
  transferType: number;
  direction: number;
  sourceType: number;
  sourceId: string;
  sourceName: string;
  destinationType: number;
  destinationId: string;
  destinationName: string;
  timestamp: string;
  notes: string;
  referenceId: string;
  userId: string;
  userName: string;
}

interface MovementHistoryResponse {
  success: boolean;
  message: string;
  data: {
    items: MovementHistoryItem[];
    pagination: {
      currentPage: number;
      pageSize: number;
      totalCount: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  };
  token: null;
}

const BoxManagementPage: React.FC = () => {
  const { boxBarcode } = useParams<{ boxBarcode: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { printBoxLabel, isPrinting, formatBoxDataForPrinting } = usePrint();
  const user = useAppSelector(s => s.user.user);
  const userRole = user?.role as ROLE;
  const userId = user?.id;

  const [boxDetails, setBoxDetails] = useState<BoxDetails | null>(null);
  const [storageUnits, setStorageUnits] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  const [showZeros, setShowZeros] = useState(false);
  const [reassignItemsDialog, setReassignItemsDialog] = useState<ReassignItemsDialogData>({
    open: false,
    selectedStorageUnitId: '',
    targetBoxBarcode: '',
    availableBoxes: [],
    searchingBoxes: false,
    selectedItems: []
  });
  const [operationLoading, setOperationLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [movementHistory, setMovementHistory] = useState<MovementHistoryItem[]>([]);


  useEffect(() => {
    console.log('BoxManagementPage mounted with boxBarcode:', boxBarcode);
    if (boxBarcode) {
      console.log('Starting to load initial data...');
      loadInitialData();
    } else {
      console.error('No boxBarcode provided');
      setError('No box barcode provided');
      setLoading(false);
    }
  }, [boxBarcode]);

  useEffect(() => {
    if (boxBarcode && !loading) {
      loadBoxDetails();
    }
  }, [showZeros]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading box details for:', boxBarcode);
      await loadBoxDetails();
      
      const [storageUnitsResponse] = await Promise.all([
        LovService.getLovDictionary('all-storage-units').catch((err) => {
          console.warn('Failed to load storage units:', err);
          return {};
        })
      ]);
      
      console.log('Storage units loaded:', storageUnitsResponse);
      
      setStorageUnits(storageUnitsResponse);
      
    } catch (error) {
      console.error('Failed to load initial data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load box information');
    } finally {
      setLoading(false);
    }
  };

const fetchBoxTransferActivity = useCallback(
  async (params: {
    page: number;
    pageSize: number;
    sortModel?: any;
    filterModel?: any;
    search?: string;
  }) => {
    if (!boxBarcode) {
      return {
        items: [],
        totalCount: 0,
        pageNumber: 1,
        pageSize: params.pageSize,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false,
      };
    }

    try {
      const query = new URLSearchParams({
        page: String(params.page + 1),
        pageSize: String(params.pageSize),
      });

      if (params.search && params.search.trim()) {
        query.append('search', params.search.trim());
      }

      if (params.sortModel && Array.isArray(params.sortModel) && params.sortModel.length > 0) {
        const sortItem = params.sortModel[0];
        if (sortItem?.field) {
          query.append('sortBy', sortItem.field);
          query.append('sortDirection', sortItem.sort || 'desc');
        }
      } else {
        query.append('sortBy', 'timestamp');
        query.append('sortDirection', 'desc');
      }

      const res = await DataService.get(`api/Operation/box/${encodeURIComponent(boxBarcode)}/transfers?${query.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}: failed to fetch transfers`);

      const result = await res.json();

      if (result && result.items) {
        // Map the new API response to the existing TransferActivityRow structure
        const mappedItems = result.items.map((item: any) => ({
          id: item.id,
          unitId: item.unitId,
          itemId: item.unitId, // Use unitId as itemId for backward compatibility
          itemName: item.itemName || 'Unknown Product',
          itemBarcode: item.itemBarcode,
          unitBarcode: item.unitBarcode || item.barcode,
          quantity: item.quantity,
          direction: item.direction,
          transferType: item.transferType || 1,
          sourceType: item.sourceType,
          sourceId: item.sourceId,
          sourceName: item.sourceName || 'Unknown Source',
          destinationType: item.destinationType,
          destinationId: item.destinationId,
          destinationName: item.destinationName || 'Unknown Destination',
          timestamp: item.timestamp,
          notes: item.notes,
          referenceId: item.referenceId,
          userId: item.userId,
          userName: item.userName || 'Unknown User'
        }));

        return {
          items: mappedItems,
          totalCount: result.totalCount || result.items?.length || 0,
          pageNumber: result.pageNumber || params.page + 1,
          pageSize: result.pageSize || params.pageSize,
          totalPages: result.totalPages || Math.ceil((result.items?.length || 0) / params.pageSize),
          hasNext: result.hasNext || false,
          hasPrevious: result.hasPrevious || false,
        };
      }

      return {
        items: [],
        totalCount: 0,
        pageNumber: params.page + 1,
        pageSize: params.pageSize,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false,
      };
    } catch (e) {
      console.error('fetchBoxTransferActivity error:', e);
      return {
        items: [],
        totalCount: 0,
        pageNumber: params.page + 1,
        pageSize: params.pageSize,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false,
      };
    }
  },
  [boxBarcode]
);


const activityColumns: PaginatedDataGridColumn[] = useMemo(
  () => [
    { field: 'timestamp', headerName: 'Date & Time', width: 180, type: 'dateTime' },
    {
      field: 'direction',
      headerName: 'Direction',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value === 1 ? 'Incoming' : 'Outgoing'}
          color={params.value === 1 ? 'success' : 'error'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'itemName',
      headerName: 'Product',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            cursor: params.row.itemId ? 'pointer' : 'default',
            '&:hover': { textDecoration: params.row.itemId ? 'underline' : 'none' },
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (params.row.itemId) navigate(`/Units/${params.row.unitId ?? params.row.itemId}`);
          }}
        >
          <Typography variant="body2" fontWeight="medium">
            {params.value || 'Unknown Product'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {(params.row.itemBarcode as string) || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Typography color={params.row.direction === 1 ? 'success.main' : 'error.main'} fontWeight="medium">
          {params.row.direction === 1 ? `+${params.value}` : `${params.value}`}
        </Typography>
      ),
    },
    { field: 'sourceName', headerName: 'From', flex: 1, minWidth: 160 },
    { field: 'destinationName', headerName: 'To', flex: 1, minWidth: 160 },
    { field: 'userName', headerName: 'User', width: 140 },
  ],
  [navigate]
);

  const loadBoxDetails = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (showZeros) queryParams.append('includeZeros', 'true');
      if (userRole === ROLE.CLIENT && userId) queryParams.append('clientId', userId);

      const queryString = queryParams.toString();
      const url = `api/BoxManagement/contents/${boxBarcode}${queryString ? `?${queryString}` : ''}`;

      const response = await DataService.get(url);

      if (response.ok) {
        const result = await response.json();
        console.log('API Response data:', result);

        if (result.success && result.data) {
          console.log('Setting box details:', result.data);
          setBoxDetails(result.data);
          return result.data;
        } else {
          throw new Error(result.message || 'Failed to load box details');
        }
      } else {
        const errorText = await response.text();
        console.error('API Error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText || 'Failed to fetch box details'}`);
      }
    } catch (error) {
      console.error('Error loading box details:', error);
      throw error;
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadBoxDetails();
      dispatch(showAlert({ message: 'Box details refreshed successfully', type: 'success' }));
    } catch (error) {
      dispatch(showAlert({ message: 'Failed to refresh box details', type: 'error' }));
    } finally {
      setRefreshing(false);
    }
  };

  const generateNewBoxBarcode = async (): Promise<string | null> => {
    try {
      const response = await DataService.post('api/BoxManagement/generate-barcode', '');
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data?.boxBarcode) {
          return result.data.boxBarcode;
        }
      }
    } catch (error) {
      console.error('Failed to generate box barcode:', error);
    }
    return null;
  };

  // const handleRegenerateBoxBarcode = async () => {
  //   setOperationLoading(true);
  //   try {
  //     const newBarcode = await generateNewBoxBarcode();
  //     if (!newBarcode) {
  //       throw new Error('Failed to generate new box barcode');
  //     }

  //     const payload = {
  //       oldBoxBarcode: boxBarcode,
  //       newBoxBarcode: newBarcode,
  //       storageUnitId: boxDetails?.storageUnitId || ''
  //     };

  //     const response = await DataService.post('api/BoxManagement/reassign', payload);
  //     const result = await response.json();
      
  //     if (response.ok && result.success) {
  //       dispatch(showAlert({ 
  //         message: `Box barcode regenerated successfully. New barcode: ${newBarcode}`, 
  //         type: 'success' 
  //       }));
        
  //       // Navigate to the new box barcode
  //       navigate(`/box-management/${newBarcode}`);
  //     } else {
  //       throw new Error(result.message || 'Regenerate barcode operation failed');
  //     }
  //   } catch (error) {
  //     console.error('Regenerate barcode operation failed:', error);
  //     dispatch(showAlert({ 
  //       message: error instanceof Error ? error.message : 'Regenerate barcode operation failed', 
  //       type: 'error' 
  //     }));
  //   } finally {
  //     setOperationLoading(false);
  //   }
  // };


  const handleReassignItemsClick = (selectedIds: string[]) => {
    if (!boxDetails || !selectedIds || selectedIds.length === 0) return;

    const selectedBoxItems = selectedIds
      .map(k => {
        const lastDash = k.lastIndexOf('-');
        if (lastDash === -1) return null;
        const idx = Number(k.slice(lastDash + 1));
        const item = boxDetails.items[idx];
        if (!item) return null;
        return { itemId: item.itemId, quantity: item.quantity, maxQuantity: item.quantity };
      })
      .filter(Boolean) as { itemId: string; quantity: number; maxQuantity: number }[];

    if (selectedBoxItems.length === 0) return;

    setReassignItemsDialog({
      open: true,
      selectedStorageUnitId: '',
      targetBoxBarcode: '',
      availableBoxes: [],
      searchingBoxes: false,
      selectedItems: selectedBoxItems,
    });
  };

  const handleStorageUnitChange = async (storageUnitId: string) => {
    setReassignItemsDialog(prev => ({
      ...prev,
      selectedStorageUnitId: storageUnitId,
      searchingBoxes: true,
      availableBoxes: [],
      targetBoxBarcode: ''
    }));

    try {
      const response = await LovService.getLovDictionary(`${storageUnitId}/boxes`);
      const boxesList = Object.keys(response);
      
      setReassignItemsDialog(prev => ({
        ...prev,
        availableBoxes: boxesList,
        searchingBoxes: false
      }));
    } catch (error) {
      console.error('Failed to load boxes for storage unit:', error);
      setReassignItemsDialog(prev => ({
        ...prev,
        availableBoxes: [],
        searchingBoxes: false
      }));
      dispatch(showAlert({ 
        message: 'Failed to load boxes for the selected storage unit', 
        type: 'warning' 
      }));
    }
  };

  const handleReassignItems = async () => {
    if (!reassignItemsDialog.selectedStorageUnitId || !reassignItemsDialog.targetBoxBarcode) return;
    
    setOperationLoading(true);
    try {
      let targetBarcode = reassignItemsDialog.targetBoxBarcode;
      
      // If no target barcode specified, generate a new one
      if (!targetBarcode || targetBarcode === 'GENERATE_NEW') {
        targetBarcode = await generateNewBoxBarcode();
        if (!targetBarcode) {
          throw new Error('Failed to generate new box barcode');
        }
      }

      const payload = {
        sourceBoxBarcode: boxBarcode,
        targetBoxBarcode: targetBarcode,
        targetStorageUnitId: reassignItemsDialog.selectedStorageUnitId,
        unitsToMove: reassignItemsDialog.selectedItems.map(item => ({
          unitId: item.itemId,
          quantity: item.quantity
        }))
      };

      const response = await DataService.post('api/BoxManagement/reassign-items', payload);
      const result = await response.json();
      
      if (response.ok && result.success) {
        dispatch(showAlert({ 
          message: `Items reassigned successfully to box: ${targetBarcode}`, 
          type: 'success' 
        }));
        setReassignItemsDialog({
          open: false,
          selectedStorageUnitId: '',
          targetBoxBarcode: '',
          availableBoxes: [],
          searchingBoxes: false,
          selectedItems: []
        });
        setSelectedItems([]);
        await loadBoxDetails();
      } else {
        throw new Error(result.message || 'Reassign items operation failed');
      }
    } catch (error) {
      console.error('Reassign items operation failed:', error);
      dispatch(showAlert({ 
        message: error instanceof Error ? error.message : 'Reassign items operation failed', 
        type: 'error' 
      }));
    } finally {
      setOperationLoading(false);
    }
  };

  const handlePrintLabel = async () => {
    if (!boxDetails) return;
    
    const printOptions = formatBoxDataForPrinting({
      barcode: boxDetails.boxBarcode,
      storageUnitName: boxDetails.storageUnitId,
      totalProducts: boxDetails.uniqueProductCount,
      totalQuantities: boxDetails.totalQuantity,
      createdDate: boxDetails.lastUpdated,
      labelPaperSize: 'SMALL_5x2_5' as LabelPaperSize
    });
    
    await printBoxLabel(printOptions);
  };

    // Transform items array to object for EnhancedTable
  const tableData = useMemo(() => {
    console.log('Box details items:', boxDetails?.items);
    if (!boxDetails?.items || !Array.isArray(boxDetails.items)) {
      console.warn('No items found in boxDetails');
      return {};
    }

    const data = boxDetails.items.reduce((acc, item, index) => {
      console.log('Processing item:', item);
      const key = `${item.itemId}-${index}`;
      acc[key] = {
        ...item,
        id: key // Add an id property for the table
      };
      return acc;
    }, {} as Record<string, BoxItem & { id: string }>);

    console.log('Table data:', data);
    return data;
  }, [boxDetails?.items]);


  // Fix the table columns with proper typing and render functions
  const tableColumns: EnhancedTableColumn[] = [
    {
      id: 'itemBarcode',
      label: 'Barcode',
      type: EnhancedTableColumnType.CUSTOM,
      sortable: false,
      render: (value: any, row: any) => {
        const item = row as BoxItem;
        return (
          <Typography variant="body2" fontFamily="monospace">
            {item.itemBarcode || '-'}
          </Typography>
        );
      },
    },
    {
      id: 'itemName',
      label: 'Product Name',
      type: EnhancedTableColumnType.CUSTOM,
      sortable: true,
      render: (value: any, row: any) => {
        const item = row as BoxItem;
        return (
          <Typography variant="body2" fontWeight={500}>
            {item.itemName}
          </Typography>
        );
      },
    },
    {
      id: 'itemSymbol',
      label: 'Symbol',
      type: EnhancedTableColumnType.CUSTOM,
      sortable: false,
      render: (value: any, row: any) => {
        const item = row as BoxItem;
        return (
          <Typography variant="body2" fontFamily="monospace">
            {item.itemSymbol || '-'}
          </Typography>
        );
      },
    },
    {
      id: 'details',
      label: 'Details',
      type: EnhancedTableColumnType.CUSTOM,
      sortable: false,
      render: (value: any, row: any) => {
        const item = row as BoxItem;
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {item.additionalProperties && Object.entries(item.additionalProperties).slice(0, 3).map(([key, value]) => (
              <Chip key={key} size="small" label={`${key}: ${value}`} variant="outlined" />
            ))}
          </Box>
        );
      },
    },
    {
      id: 'location',
      label: 'Location',
      type: EnhancedTableColumnType.CUSTOM,
      sortable: false,
      render: (value: any, row: any) => {
        const item = row as BoxItem;
        return (
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {item.warehouseName || '-'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item.storageUnitName || '-'} ({item.storageUnitCode || '-'})
            </Typography>
          </Box>
        );
      },
    },
    {
      id: 'quantity',
      label: 'Qty',
      type: EnhancedTableColumnType.CUSTOM,
      sortable: true,
      align: 'center' as const,
      render: (value: any, row: any) => {
        const item = row as BoxItem;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Badge badgeContent={item.quantity} color="primary">
              <Inventory2 />
            </Badge>
          </Box>
        );
      },
    },
    {
      id: 'actions',
      label: 'Actions',
      type: EnhancedTableColumnType.CUSTOM,
      sortable: false,
      align: 'center' as const,
      render: (value: any, row: any) => {
        const item = row as BoxItem;
        return (
          <Tooltip title="View Product">
            <IconButton 
              size="small" 
              onClick={() => navigate(`/Units/${item.itemId}`)}
              color="primary"
            >
              <Launch fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  const tableActions = [
    {
      key: 'reassign-items',
      title: 'Reassign Selected Items',
      icon: <MoveToInbox />,
      onClick: (selectedIds: string[]) => handleReassignItemsClick(selectedIds),
      visible: (selectedIds: string[]) => selectedIds.length > 0,
      disabled: (selectedIds: string[]) => selectedIds.length === 0,
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Loader 
          type="warehouse"
          size="large"
          message="Loading Box Details..."
          subMessage="Fetching box contents and location"
          showProgress={true}
        />
      </Box>
    );
  }

  if (error || !boxDetails) {
    return (
      <StatusCard
        variant="error"
        title="Error Loading Box"
        message={error || 'Box not found'}
        onAction={() => navigate(-1)}
        Icon={ArrowBack}
      />
    );
  }
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate(-1)} color="primary">
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Box Management
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={showZeros ? 'Hide Zero Quantities' : 'Show Zero Quantities'}>
              <IconButton onClick={() => setShowZeros(p => !p)} color="primary">
                {showZeros ? <FilterAltOffIcon /> : <FilterAltIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} disabled={refreshing}>
                {refreshing ? <CircularProgress size={20} /> : <Refresh />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Print Label">
              <IconButton onClick={handlePrintLabel} color="primary" disabled={isPrinting}>
                {isPrinting ? <CircularProgress size={20} /> : <Print />}
              </IconButton>
            </Tooltip>
            {/* <Tooltip title="Regenerate Box Barcode">
              <Button
                variant="outlined"
                startIcon={operationLoading ? <CircularProgress size={16} /> : <Autorenew />}
                onClick={handleRegenerateBoxBarcode}
                disabled={operationLoading}
              >
                {operationLoading ? 'Regenerating...' : 'Regenerate Barcode'}
              </Button>
            </Tooltip> */}
          </Box>
        </Box>

        {/* Box Overview Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <QrCode color="primary" fontSize="large" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">Box Information</Typography>
                <Typography variant="body2" color="text.secondary">
                  Barcode: {boxDetails.boxBarcode}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 4 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                    {boxDetails.totalQuantity}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Quantity
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="secondary" sx={{ fontWeight: 'bold' }}>
                    {boxDetails.uniqueProductCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Unique Products
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={(_, newTab) => setCurrentTab(newTab)}
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTabs-flexContainer': {
              gap: 2
            }
          }}
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Inventory2 fontSize="small" />
                <Typography>Box Contents</Typography>
                <Chip 
                  label={boxDetails?.items.length || 0} 
                  size="small" 
                  sx={{ ml: 1 }}
                />
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Timeline fontSize="small" />
                <Typography>Movement History</Typography>
                <Chip 
                  label={movementHistory.length} 
                  size="small" 
                  sx={{ ml: 1 }}
                />
              </Box>
            } 
          />
        </Tabs>
        
        <Box sx={{ p: 3 }}>
          {/* Items Tab Content */}
          {currentTab === 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6">Box Contents</Typography>
                <Typography variant="body2" color="text.secondary">
                  {boxDetails?.items?.length || 0} product types
                </Typography>
              </Box>

              {boxDetails?.items && boxDetails.items.length > 0 ? (
                <EnhancedTable
                  data={tableData}
                  columns={tableColumns}
                  title=""
                  emptyMessage="No items in this box"
                  selection={{ showCheckbox: true, selectionMode: 'multiple' }}
                  actions={tableActions}
                  onStateChange={(state) => {
                    setSelectedItems(state.selected);
                  }}
                  wmsMode={true}
                  densityLevel="compact"
                  stickyHeader
                  maxHeight={400}
                />
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No items found in this box
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          
        {currentTab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimelineIcon fontSize="small" />
                <Typography variant="h6">Movement History</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {movementHistory.length} (live grid)
              </Typography>
            </Box>

            <PaginatedDataGrid
              key={`box-activity-${activityRefreshKey}-${boxBarcode}`}
              title="Transfer Activity"
              columns={activityColumns}
              idField="id"
              useBackendPagination
              onDataFetch={fetchBoxTransferActivity}
              wmsMode
              densityLevel="standard"
              striped
              maxHeight={600}
              initialState={{
                pagination: { paginationModel: { pageSize: 15 } },
                sorting: { sortModel: [{ field: 'timestamp', sort: 'desc' }] },
              }}
            />
          </Box>
        )}
        </Box>
      </Paper>

      {/* Reassign Items Dialog */}
      <Dialog 
        open={reassignItemsDialog.open} 
        onClose={() => setReassignItemsDialog({
          open: false,
          selectedStorageUnitId: '',
          targetBoxBarcode: '',
          availableBoxes: [],
          searchingBoxes: false,
          selectedItems: []
        })} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>Reassign Selected Items</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Select a target storage unit and box to move the selected items. Leave box barcode empty to create a new box.
          </Alert>
          
          <Autocomplete
            options={Object.entries(storageUnits)}
            getOptionLabel={([id, name]) => name}
            value={storageUnits[reassignItemsDialog.selectedStorageUnitId] ? 
              [reassignItemsDialog.selectedStorageUnitId, storageUnits[reassignItemsDialog.selectedStorageUnitId]] : null}
            onChange={(_, newValue) => {
              if (newValue) {
                handleStorageUnitChange(newValue[0]);
              } else {
                setReassignItemsDialog(prev => ({
                  ...prev,
                  selectedStorageUnitId: '',
                  availableBoxes: [],
                  targetBoxBarcode: ''
                }));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Target Storage Unit"
                required
                placeholder="Select storage unit..."
              />
            )}
            sx={{ mb: 3 }}
          />

          {/* Box Selection - only show when storage unit is selected */}
          {reassignItemsDialog.selectedStorageUnitId && (
            <Box sx={{ mb: 3 }}>
              {reassignItemsDialog.searchingBoxes ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading boxes...</Typography>
                </Box>
              ) : (
                <Autocomplete
                  options={['GENERATE_NEW', ...reassignItemsDialog.availableBoxes]}
                  getOptionLabel={(option) => 
                    option === 'GENERATE_NEW' ? 'Generate New Box' : option
                  }
                  value={reassignItemsDialog.targetBoxBarcode || null}
                  onChange={(_, newValue) => {
                    setReassignItemsDialog(prev => ({
                      ...prev,
                      targetBoxBarcode: newValue || ''
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Target Box Barcode"
                      placeholder="Select existing box or generate new..."
                      helperText="Select 'Generate New Box' to create a new box barcode"
                    />
                  )}
                  freeSolo
                  onInputChange={(_, newValue) => {
                    setReassignItemsDialog(prev => ({
                      ...prev,
                      targetBoxBarcode: newValue || ''
                    }));
                  }}
                />
              )}
            </Box>
          )}

          {/* Selected Items Summary */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Items to Reassign ({reassignItemsDialog.selectedItems.length})
          </Typography>
          {reassignItemsDialog.selectedItems.map((selectedItem) => {
            const item = boxDetails?.items.find(i => i.itemId === selectedItem.itemId);
            if (!item) return null;
            
            return (
              <Box 
                key={selectedItem.itemId} 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  mb: 2, 
                  p: 2, 
                  border: 1, 
                  borderColor: 'divider', 
                  borderRadius: 1 
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {item.itemName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Barcode: {item.itemBarcode} | Available: {item.quantity}
                  </Typography>
                </Box>
                <TextField
                  type="number"
                  label="Quantity to Move"
                  size="small"
                  value={selectedItem.quantity}
                  onChange={(e) => {
                    const quantity = Math.max(0, Math.min(item.quantity, parseInt(e.target.value) || 0));
                    setReassignItemsDialog(prev => ({
                      ...prev,
                      selectedItems: prev.selectedItems.map(si => 
                        si.itemId === selectedItem.itemId 
                          ? { ...si, quantity }
                          : si
                      )
                    }));
                  }}
                  inputProps={{ min: 0, max: item.quantity }}
                  sx={{ width: 120 }}
                />
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setReassignItemsDialog({
              open: false,
              selectedStorageUnitId: '',
              targetBoxBarcode: '',
              availableBoxes: [],
              searchingBoxes: false,
              selectedItems: []
            })}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleReassignItems} 
            variant="contained" 
            disabled={
              !reassignItemsDialog.selectedStorageUnitId || 
              reassignItemsDialog.selectedItems.length === 0 || 
              operationLoading ||
              reassignItemsDialog.searchingBoxes
            }
            startIcon={operationLoading ? <CircularProgress size={16} /> : <MoveToInbox />}
          >
            {operationLoading ? 'Reassigning...' : 'Reassign Items'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BoxManagementPage;